import { apiService } from '../api.service';
import { getPartnerName } from '../../../utils/partner/partner.util';

import { URL } from './signaller.service.config';

import type { IInfiniteQueryParams } from '../../../types/api.type';
import type { TAvailableValuesType } from '../../../types/components/components.type';
import type {
  IGetCommentsResponse,
  IGetPublicKey,
  IGetSignalerCaseResponse,
  IPostCommentsParams,
  IPostCommentsResponse,
  IPostEmail,
  IPostEncryptPdf,
  IPostEncryptPdfReturn,
  IPostSignallerReportParams,
  IPostSignallerReportReturn,
} from './signaller.service.type';

/**
 * Get Partner public key
 *
 * {@link https://dev-openapi.safeandtrust.eu/#tag/signaller/operation/getSignallerPublicKey API Docs}
 */
export const getPublicKey = async (): Promise<IGetPublicKey> => {
  const { data } = await apiService.get<IGetPublicKey>(URL.getPublicKey, {});

  return data;
};

/**
 * Upload signaller report
 *
 * {@link https://dev-openapi.safeandtrust.eu/#tag/signaller/operation/createSignallerFile API Docs}
 */
export const postSignallerReport = async (
  data: IPostSignallerReportParams,
): Promise<IPostSignallerReportReturn[]> => {
  const { data: returnData } = await apiService.post<
    IPostSignallerReportReturn[],
    never,
    IPostSignallerReportParams
  >(URL.postSignallerReport, data);

  return returnData;
};

/**
 * Send E-mail to reporter
 *
 * {@link https://dev-openapi.safeandtrust.eu/#tag/signaller/operation/sendSignallerEmail API Docs}
 */
export const postEmail = async (data: IPostEmail): Promise<void> => {
  await apiService.post<never, never, IPostEmail>(URL.postEmail, data);
};

/**
 * Encrypt Signaller's pdf file
 *
 * {@link https://dev-openapi.safeandtrust.eu/#tag/signaller/operation/signallerEncryptPdf API Docs}
 */
export const postEncryptPdf = async (data: IPostEncryptPdf): Promise<IPostEncryptPdfReturn> => {
  const { data: returnData } = await apiService.post<never, never, IPostEncryptPdfReturn>(
    URL.postEncryptPdf,
    data,
  );

  return returnData;
};

/**
 * Returns list of available subjects for report
 *
 * {@link unknown API Docs}
 */
export const getSubjects = async (): Promise<TAvailableValuesType> => {
  const { data: returnData } = await apiService.get<TAvailableValuesType>(URL.getSubjects);

  return returnData;
};

/**
 * Get case status and last update time
 *
 * {@link https://dev-openapi.safeandtrust.eu/#tag/signaller/operation/getSignallerCase API Docs}
 */
export const getSignalerCase = async (): Promise<IGetSignalerCaseResponse> => {
  const { data } = await apiService.get<IGetSignalerCaseResponse>(URL.getSignalerCase);

  return data;
};

/**
 * Get comments for signaller case - not secret ones.
 *
 * {@link https://dev-openapi.safeandtrust.eu/#tag/signaller/operation/getSignallerComment API Docs}
 */
export const getComments = async ({
  pageSize,
  lastPageParam,
}: IInfiniteQueryParams): Promise<IGetCommentsResponse> => {
  const { data } = await apiService.get<IGetCommentsResponse>(URL.getComments, {
    params: {
      limit: pageSize,
      lastEvaluatedKey: lastPageParam || undefined,
    },
  });

  return data;
};

/**
 * Add comments and attachments to document of active investigator. Returns list of created files with url to upload.
 *
 * {@link https://dev-openapi.safeandtrust.eu/#tag/signaller/operation/createSignallerComment API Docs}
 */
export const postComments = async (
  newData: Omit<IPostCommentsParams, 'client'>,
): Promise<IPostCommentsResponse[]> => {
  const { data } = await apiService.post<IPostCommentsResponse[], never, IPostCommentsParams>(
    URL.postComments,
    { ...newData, client: getPartnerName() },
  );

  return data;
};
